import React, { useState, useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { types, formatProductTypes, defaultFilterType } from './utils';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import SystemCertificateList from './SystemCertificateList';
import { FindInPage } from '@material-ui/icons';
import * as actions from './store/systemCertificateActions';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: "auto",
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down("sm")]: {
            padding: 0,
            margin: 0,
            width: "100%",
        },
    },
});

const SystemCertificate = props => {
    const tableRef = useRef();
    const { classes, translations, onFetchProductType, sendMessage, columns, activeCustomerID, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, SystemCertificateID: null, redirectPath: '' });
    const [productTypes, setProductTypes] = useState(null);

    let visible = columns && columns['SystemCertificate'];

    const typeLookup = types.reduce((obj, item) => Object.assign(obj, { [item.value]: translations[item.value] || item.label }), {});

    useEffect(() => {
        const fetchProductTypes = async () => {
            let productTypesList = await onFetchProductType({ ActiveCustomerID: activeCustomerID });
            let list = [];
            let formattedProductTypes = productTypesList.reduce((obj, item) => Object.assign(obj, { [item.key]: formatProductTypes(item.label, translations) }), {});
            if (formattedProductTypes) {
                for (const [key] of Object.entries(formattedProductTypes)) {
                    list.push(key);
                }
            }
            setProductTypes({ lookup: formattedProductTypes, defaultFilter: list });
        }
        if (!productTypes) {
            fetchProductTypes();
        }

        return () => { };
        // eslint-disable-next-line
    }, [productTypes, translations])
    
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'system_certificate_load') ? <Tooltip title={translations["system_certificate_details"] || "System certificate details"}>
                            <IconButton value={rowData?.SystemCertificateID} aria-label={translations["system_certificate_details"] || "System certificate details"} onClick={() => redirectHandler(`/admin/system-certificate/edit?SystemCertificateID=${rowData.SystemCertificateID}`)}>
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.Name ? rowData?.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["type"] || 'Type', field: 'Type',
                hidden: visible ? visible['Type'] : false,
                lookup: typeLookup,
                defaultFilter: defaultFilterType(typeLookup),
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.Type ? translations[rowData?.Type] || rowData?.Type : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["product_type"] || 'Product Type', field: 'ProductType',
                hidden: visible ? visible['ProductType'] : false,
                lookup: productTypes?.lookup,
                defaultFilter: productTypes?.defaultFilter,
                render: rowData => {
                    return rowData && (rowData?.ProductTypes ?? []).map(el => (
                        <Typography key={el.Key} variant={'caption'}>{formatProductTypes(el.Key, translations) || el.Key}<br></br> </Typography>))
                }
            }
        ];
        // eslint-disable-next-line
    }, [translations, permissions, visible, typeLookup, productTypes]);

    if (!hasAtLeastOnePermission(permissions, ['system_certificate_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, SystemCertificateID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, SystemCertificateID: SystemCertificateID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'system_certificate_list') ?
                <SystemCertificateList
                    tableRef={tableRef}
                    sendMessage={sendMessage}
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state?.translate?.translations?.activeTranslations || {},
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID || null,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchProductType: params => dispatch(actions.listProductTypes(params)),
    }
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(SystemCertificate));
